;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import { isRecord } from './is-record'
import { redactError, redactSensitiveFields } from './redact'
import { LogContext, LogInput, LogPayload } from './types'

/**
 *
 * Normalize the input from callers of the logger to a standardized structure
 *
 * Formats the combination of a message or error, plus additional optional context
 * If an `Error` is provided it will be available on the `error` key, and error.message will be available on the `message` key.
 * If a string is provided it will be available on the `msg` key.
 * The rest of the context is available on the `context` key.
 *
 * @example normalizeLogInput('Test message', { userId: '123' })
 * @example normalizeLogInput(new Error('Test error'), { userId: '123' })
 */
export function normalizeLogInput(
  /**
   * The first parameter can be a string, an Error, or an unknown value.
   *
   * This should usually be an Error or a string.
   * The unknown type is there to allow this function to be used in a catch block where you can't guarantee the type of the error.
   */
  input: LogInput,
  /**
   * Additional context to include in the log payload.
   *
   * Common sensitive fields are redacted automatically.
   *  */
  context?: LogContext,
): LogPayload {
  const basePayload = normalizePrimaryInput(input)
  const redactedContext = context ? redactSensitiveFields(context) : undefined

  const baseContext =
    'context' in basePayload && isRecord(basePayload.context)
      ? basePayload.context
      : undefined

  const mergedContext =
    baseContext || redactedContext
      ? {
          ...baseContext,
          ...redactedContext,
        }
      : undefined

  return {
    ...basePayload,
    context: mergedContext,
  }
}

/**
 * Normalizes the primary input to a standardized structure.
 *
 * If the input is just a message, put it on the `message` key of the logged object
 * If the input is an error, first redact sensitive information, then put it on the `error` key and extract
 * its message to the `message key`
 *
 * Handles the case where the input might be unknown (e.g., from catch blocks).
 */
function normalizePrimaryInput(input: string | Error | unknown) {
  if (typeof input === 'string') {
    return { message: input }
  }

  if (input instanceof Error) {
    return {
      error: Object.assign(redactError(input), {
        kind: input.name,
      }),
      message: input.message,
    }
  }

  // Handle plain objects (e.g., thrown objects or context objects)
  if (isRecord(input)) {
    return {
      message:
        'message' in input && typeof input.message === 'string'
          ? input.message
          : 'Missing message',
      context: redactSensitiveFields(input),
    }
  }

  // Handle primitive values or null/undefined
  return { message: 'Missing message' }
}
